<script setup lang="ts">
/**
 * The assessment tile: a slider over the user's questionnaires, one card at a time (IPD-3052).
 *
 * It decides nothing. The backend sends the whole list and picks the single one that is open, which
 * heads it; everything behind is locked, in the order the recommendation engine gave (IPD-2971 §5).
 * The face arrives resolved by `resolveTodayAssessments` and is rendered.
 *
 * Locked does not mean hidden: the cards behind are the next slides, visible so the user can see
 * what is coming, and simply not actionable.
 */
import {computed, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import SvgIllustration from '@/vue/atoms/svg-illustration.vue';
import TodayAssessmentCard from '@/vue/molecules/today/today-assessment-card.vue';
import {
  TodayAssessmentFace,
  type TodayAssessmentSlide,
  type TodayAssessmentView,
} from '@/ts/types/component/today-page';

const props = defineProps<{
  tile: TodayAssessmentView;
}>();

const emit = defineEmits<{
  /**
   * The user wants to answer the open assessment. An event rather than a link: a questionnaire opens
   * in the app's modal overlay, which is the page's to open — the same shape the Upcoming tile uses
   * for its own onboarding CTA.
   *
   * Nothing listens to it yet, and that is not an oversight: the page has no questionnaire to hand
   * an overlay until it reads GET /me/today, whose endpoint exists on the backend but is neither
   * merged nor called from here. Wiring it is not this ticket, and has no ticket of its own yet.
   */
  select: [slide: TodayAssessmentSlide];
}>();

const {t: $t} = useI18n();

/**
 * The line above the slider, which only the first-use face has. Its copy deliberately does not name
 * the onboarding questionnaire (decided 2026-07-07): its CTA is in the Upcoming tile at that
 * moment, and a second mention of it here would read as a second thing to do. The countdown face
 * carries no line of its own — the card states when it reopens and the prototype shows nothing
 * above it.
 */
const LEAD_KEY: Partial<Record<TodayAssessmentFace, string>> = {
  [TodayAssessmentFace.locked]: 'today_assessment_locked_body',
};

const viewport = ref<HTMLElement | null>(null);

/** Which card is on screen, read back from where the container actually is. */
const index = ref(0);

/**
 * Where it was last asked to go. Kept apart from {@link index} because a smooth scroll takes time to
 * arrive, and until it does the index still reads the card being left: two quick presses of Next
 * would both be computed from it and the second would go nowhere.
 */
const target = ref(0);

const lead = computed<string | null>(() => {
  const key = LEAD_KEY[props.tile.face];
  return key ? $t(key) : null;
});

/**
 * The pointer and the keyboard are counted apart. One flag for both meant tabbing away while the
 * mouse was still on the card read as the mouse leaving, and vice versa.
 */
const isHovered = ref(false);
const isFocused = ref(false);
const isPointedAt = computed<boolean>(() => isHovered.value || isFocused.value);

/**
 * Focus counts as pointing at the tile only when it came from the keyboard.
 *
 * A press of an arrow leaves that button focused, and counting a focus the mouse left behind kept
 * the tile "pointed at" after the pointer had gone: the drawing stayed played out, and leaving and
 * coming back replayed nothing until something outside took the focus away. `:focus-visible` is the
 * browser's own answer to "was this focus deliberate", and the same rule the design system's focus
 * ring already uses. Where it is not implemented the animation simply stops answering the keyboard,
 * which is the safe way for a decoration to degrade.
 */
function onFocusIn(event: FocusEvent): void {
  const {target: focused} = event;
  isFocused.value = focused instanceof Element && focused.matches(':focus-visible');
}

/**
 * `focusout` bubbles, so moving focus from the viewport to an arrow — inside the same tile — used to
 * read as leaving it: every Tab press inside the tile stopped the artwork and started it again.
 */
function onFocusOut(event: FocusEvent): void {
  const next = event.relatedTarget;
  if (next instanceof Node && (event.currentTarget as HTMLElement | null)?.contains(next) === true) {
    return;
  }
  isFocused.value = false;
}

// One card is not a slider. The arrows appear with the second card, exactly as the design draws it.
const hasSlider = computed<boolean>(() => props.tile.slides.length > 1);

const atStart = computed<boolean>(() => index.value === 0);
const atEnd = computed<boolean>(() => index.value >= props.tile.slides.length - 1);

/**
 * Paging moves the scroll container rather than a transform, so a touch swipe and a keyboard both
 * work without a line of code for either, and the position is read back from where the container
 * actually is instead of from a counter a swipe would leave behind.
 *
 * The smoothness is decided here and not in the stylesheet: an explicit `behavior` in the options
 * overrides the computed `scroll-behavior`, so a reduced-motion rule in CSS would be declared and
 * never applied to the one thing that scrolls this container.
 */
function page(step: number): void {
  const element = viewport.value;
  // Guarded rather than disabled: the arrows keep their focus at the ends (see the template), so a
  // press there has to be answered with nothing rather than prevented.
  if (!element || (step < 0 && atStart.value) || (step > 0 && atEnd.value)) {
    return;
  }
  const next = Math.min(Math.max(target.value + step, 0), props.tile.slides.length - 1);
  target.value = next;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  element.scrollTo({left: next * element.clientWidth, behavior: reduced ? 'auto' : 'smooth'});
}

function onScroll(): void {
  const element = viewport.value;
  if (element && element.clientWidth > 0) {
    index.value = Math.round(element.scrollLeft / element.clientWidth);
    // Adopted only once the container has arrived where it was sent. A smooth scroll reports itself
    // continuously, and for the first half of it the rounded index is still the card being left: set
    // unconditionally, this wrote that stale card back over the pending target within a frame, so a
    // second quick press of Next computed from where the slider was rather than where it was going
    // and went nowhere — the very thing `target` exists to prevent. However the container got there
    // otherwise — a swipe, a keyboard — the two agree and this simply keeps them agreeing.
    if (index.value === target.value) {
      return;
    }
    // A move nobody asked for (a swipe, a drag) still has to be taken as the new place to page from.
    // It is told apart from a pass through the middle of a smooth scroll by the container having come
    // to rest: mid-flight, an offset sits between two cards rather than on one.
    const settled = Math.abs(element.scrollLeft - index.value * element.clientWidth) < 1;
    if (settled) {
      target.value = index.value;
    }
  }
}
</script>

<template>
  <!-- The pointer is watched here rather than on the card, because the arrows sit over the card
       without being inside it: crossing one used to read as leaving, so the artwork stopped and
       restarted on the way to the very button that pages it. The tile is what the user is pointing
       at, and the tile is what decides. -->
  <section
    class="assessment-tile"
    :aria-label="$t('assessment')"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
    @focusin="onFocusIn"
    @focusout="onFocusOut"
  >
    <!-- Focusable in its own right while it can scroll: a scrolling region has to be reachable by
         keyboard, and the faces with nothing open hold no focusable card to reach it through. It
         also hands the arrow keys the slider without going by way of the buttons. -->
    <div
      ref="viewport"
      class="assessment-tile__viewport"
      :tabindex="hasSlider ? 0 : undefined"
      @scroll.passive="onScroll"
    >
      <ul class="assessment-tile__track">
        <!-- Undefined rather than false for the current one: `inert` is not treated as a boolean
             attribute here, so a false would render as inert="false" — which the DOM reads as
             present, and the card on screen would go inert with it. -->
        <li
          v-for="(slide, position) in tile.slides"
          :key="slide.id"
          class="assessment-tile__slide"
          :inert="position === index ? undefined : true"
        >
          <!-- The lead line and a card's padlock say the same thing and land in the same corner, so
               the cards drop theirs while it is on screen. It only appears on the first-use face,
               where every card is locked anyway and one sentence covers the lot; on every other
               face there is no line and each locked card carries its own mark. -->
          <today-assessment-card
            :slide="slide"
            :on-screen="position === index"
            :pointed-at="isPointedAt"
            :lock-announced="Boolean(lead)"
            @select="emit('select', $event)"
          />
        </li>
      </ul>
    </div>

    <!-- Sits over the card rather than above it: the card fills the tile, and this is the tile
         speaking about the whole slider rather than about the one on screen.
         It carries the padlock itself on this face. Every card behind it is locked, so one mark
         beside the sentence says what a mark per card would have said several times over — and the
         two would land in the same corner, which is what they did before this. -->
    <p v-if="lead" class="assessment-tile__lead">
      <span class="assessment-tile__lock" aria-hidden="true">
        <svg-illustration name="lock.svg"/>
      </span>
      <span>{{ lead }}</span>
    </p>

    <template v-if="hasSlider">
      <div class="assessment-tile__arrows">
        <!-- aria-disabled, not disabled: at the ends, `disabled` takes the focus off the very button
             the user has just reached with the keyboard, and the browser drops them back to the top
             of the document. The press is answered by doing nothing instead. -->
        <button
          type="button"
          class="assessment-tile__arrow"
          :aria-disabled="atStart"
          :aria-label="$t('today_assessment_prev')"
          @click="page(-1)"
        >
          <svg-illustration name="chevron-left.svg" aria-hidden="true"/>
        </button>
        <button
          type="button"
          class="assessment-tile__arrow assessment-tile__arrow--next"
          :aria-disabled="atEnd"
          :aria-label="$t('today_assessment_next')"
          @click="page(1)"
        >
          <svg-illustration name="chevron-left.svg" aria-hidden="true"/>
        </button>
      </div>

      <!-- Where the user is, said out loud. An arrow gone quiet says there is nothing past it, not
           which card is on screen, and nothing else announces the change after a page. The dots
           below say the same thing visually and stay out of the reader's way. -->
      <p class="sr-only" aria-live="polite">
        {{ $t('today_assessment_position', {current: index + 1, total: tile.slides.length}) }}
      </p>

      <div class="assessment-tile__dots" aria-hidden="true">
        <span
          v-for="(slide, dot) in tile.slides"
          :key="slide.id"
          class="assessment-tile__dot"
          :class="{'assessment-tile__dot--on': dot === index}"
        />
      </div>
    </template>
  </section>
</template>

<style scoped lang="scss">
// The card fills the tile edge to edge and brings its own colour, so the tile itself is only the
// rounded window that clips it, plus the chrome that floats on top: the copy line, the arrows and
// the dots. All three sit ON a card tint that does not follow the scheme, so all three take the
// pinned ink rather than the theme-aware one.
.assessment-tile {
  position: relative;
  display: flex;
  min-width: 0;
  border-radius: var(--today-radius-section);
  color: var(--today-assessment-ink);
  overflow: hidden;
}

// The padlock and the sentence read as one line, so they are one: a row rather than two things
// anchored to the same corner. Centred on each other while the sentence fits on a line, and on the
// block of it when a narrow tile wraps it — the alternative, pinning the disc to the first line,
// leaves it floating against two lines of text.
.assessment-tile__lead {
  @include font-body;

  position: absolute;
  // The same step from the top as from the sides, so the mark sits square in its corner. The
  // design's own numbers are 27 and 40, close to this pair at the widest breakpoint but tighter at
  // the narrow ones, where the disc nearly fills the gap it is given.
  top: var(--Spacing-spacing-6);
  right: var(--Spacing-spacing-6);
  left: var(--Spacing-spacing-6);
  display: flex;
  gap: var(--Spacing-spacing-3);
  align-items: center;
  margin: 0;
  font-size: var(--today-text-body);
}

// The same disc the cards carry on every other face (today-assessment-card.vue), so the mark reads
// as the same mark wherever the tile puts it.
.assessment-tile__lock {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--today-assessment-scrim) 70%, transparent);

  :deep(svg) {
    width: 18px;
    height: 18px;
  }
}

// The window onto the track. It is a real scroll container rather than a transformed strip: swiping
// and keyboard scrolling then come for free, and snapping keeps it on whole cards however it is
// moved. Its scrollbar is hidden because the arrows are the affordance the design draws.
.assessment-tile__viewport {
  @include focus-visible;

  // Drawn inside its own box. The viewport fills the tile exactly and the tile clips
  // (`overflow: hidden` above, plus its radius), so a ring at the mixin's positive offset lands on
  // the parent's border and is clipped away entirely — and on the faces with no call to action this
  // scroll container is the only thing a keyboard can land on.
  //
  // And in the card's ink rather than the app's focus colour, which is the one exception this tile
  // needs: the card colours do not flip with the scheme, and the default ring measures 2.25 to
  // 2.94:1 on them, under the 3:1 a focus indicator has to reach (1.4.11). The ink gives 7.42 to
  // 9.72:1 on the five tints and 7.45:1 on the locked grey (IPD-3131). Nothing automated catches
  // this — axe does not look at focus rings.
  &:focus-visible {
    outline-color: var(--today-assessment-ink);
    outline-offset: -2px;
  }

  flex: 1;
  scroll-snap-type: x mandatory;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.assessment-tile__track {
  display: flex;
  height: 100%;
  margin: 0;
  padding: 0;
  list-style: none;
}

.assessment-tile__slide {
  display: flex;
  flex: 0 0 100%;
  scroll-snap-align: start;
  min-width: 0;
}

// Over the artwork, at the height of its middle, which is where the design puts them.
.assessment-tile__arrows {
  position: absolute;
  top: 0;
  right: var(--Spacing-spacing-2);
  bottom: 0;
  left: var(--Spacing-spacing-2);
  display: flex;
  align-items: center;
  justify-content: space-between;
  pointer-events: none;
}

// Bare chevrons, as drawn: no disc behind them, because they sit on the card's own colour.
.assessment-tile__arrow {
  @include focus-visible;

  // Same reason as the viewport's ring above: over the card's tint the app's focus colour is under
  // the 3:1 an indicator needs.
  &:focus-visible {
    outline-color: var(--today-assessment-ink);
  }

  display: grid;
  place-items: center;
  // The design's own 62 with a 42 chevron inside, measured off the prototype. Comfortably past the
  // 44px minimum target, so nothing is traded for matching it.
  width: 62px;
  height: 62px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: inherit;
  cursor: pointer;
  pointer-events: auto;
  transition: opacity 0.2s ease;

  :deep(svg) {
    width: 42px;
    height: 42px;
  }

  &[aria-disabled='true'] {
    // Faded rather than hidden, and not below this: the button keeps its focus at the ends, so what
    // a keyboard lands on has to stay visible. At 0.3 the arrow measured 1.69:1 against the tile.
    opacity: 0.45;
    cursor: default;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
}

// One chevron asset, turned around for the other direction, rather than a second file.
.assessment-tile__arrow--next :deep(svg) {
  transform: rotate(180deg);
}

// Literals, both measured on the prototype, because neither lands on a step of the scale: it sets
// the row 22 from the foot of the card and 8 between the dots. The steps nearest each resolve to a
// third of that on a narrow tile — 8 from the foot and 2 apart — which read as one smudge against
// the bottom edge rather than as a row of positions.
.assessment-tile__dots {
  position: absolute;
  right: 0;
  bottom: 22px;
  left: 0;
  display: flex;
  justify-content: center;
  gap: 8px;
  pointer-events: none;
}

.assessment-tile__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: color-mix(in srgb, var(--today-assessment-ink) 25%, transparent);
  transition: background-color 0.2s ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
}

.assessment-tile__dot--on {
  background: var(--today-assessment-ink);
}
</style>
